import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClassForm, ClassFormValues } from '@/components/classes/ClassForm';
import { ClassMutationService } from '@/services/classService';
import { useDataServices } from '@/hooks/useDataServices';
import { useAuth } from '@/hooks/useAuth';
import { Class } from '@/types/classTypes';
import { centsToDollars } from '@/utils/money';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Loader2, Save, X } from 'lucide-react';
import { getLocations } from '@/services/locationService';
import { getSeasons } from '@/services/seasonService';
import { motion } from 'framer-motion';

const EditClassPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { class: classService } = useDataServices();
  const queryClient = useQueryClient();
  const [classData, setClassData] = useState<Class | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);
  const [seasonOptions, setSeasonOptions] = useState<{ value: string; label: string }[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Get organization ID from user metadata
  const organizationId = user?.user_metadata?.organization_id || '';

  useEffect(() => {
    const fetchClassData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await classService.getClassById(id);
        setClassData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        toast.error('Failed to load class details');
      } finally {
        setLoading(false);
      }
    };
    if (id && organizationId) {
      fetchClassData();
    } else {
      setLoading(false);
    }
  }, [id, organizationId, classService]);

  useEffect(() => {
    const fetchOptions = async () => {
      if (!organizationId) return;
      setOptionsLoading(true);
      try {
        const [locations, seasons] = await Promise.all([
          getLocations(organizationId),
          getSeasons(organizationId),
        ]);
        setLocationOptions(locations.map(loc => ({ value: loc.id, label: loc.name })));
        setSeasonOptions(seasons.map(season => ({ value: season.id, label: season.name })));
      } catch (err) {
        toast.error('Failed to load location or season options');
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, [organizationId]);

  const updateClassMutation = useMutation({
    mutationFn: (values: any) => {
      if (!id) throw new Error('No class ID');
      // Convert empty string UUIDs to null
      const location_id = !values.location_id || values.location_id === '' ? null : values.location_id;
      const season_id = !values.season_id || values.season_id === '' ? null : values.season_id;
      
      // Include ALL fields that the form supports
      // Note: ClassForm already converts price_dollars to price_cents, so we use the converted value
      return ClassMutationService.updateClass(id, {
        name: values.name,
        description: values.description || '',
        location_id,
        max_students: values.capacity,
        billing_model: values.billing_model,
        price_cents: values.price_cents, // Use the already converted price_cents from ClassForm
        season_id,
        level: values.level || '',
        dress_code: values.dress_code || '',
        category: values.category || '',
        status: values.status || 'active',
        trial_available: values.trial_available || false,
        is_active: values.is_active !== undefined ? values.is_active : true,
        waitlist_enabled: values.waitlistEnabled !== undefined ? values.waitlistEnabled : true,
        schedules: values.schedules || [],
      });
    },
    onSuccess: (updatedClass) => {
      toast.success('Class updated successfully!');
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({ queryKey: ['class', id] });
      
      if (updatedClass && updatedClass.id) {
        navigate(`/admin/classes`);
      } else {
        navigate('/admin/classes');
      }
    },
    onError: (error: any) => {
      console.error('Error updating class:', error);
      toast.error('Failed to update class. Please try again.');
    },
  });

  const onSubmitClass = (values: any) => {
    updateClassMutation.mutate(values);
  };

  const handleCancel = () => {
    navigate('/admin/classes');
  };

  if (loading || optionsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading class details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto py-8">
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="text-red-600">Error Loading Class</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-gray-600">Failed to load class for editing.</p>
              <Button onClick={() => navigate('/admin/classes')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Classes
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Classes
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 shadow-sm">
              <BookOpen className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Class</h1>
              <p className="text-gray-600 mt-1">Update class details, schedule, and pricing</p>
            </div>
          </div>
        </motion.div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-4xl mx-auto"
        >
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-6 w-6" />
                  <div>
                    <CardTitle className="text-xl">Class Information</CardTitle>
                    <p className="text-blue-100 text-sm">Editing: {classData.name}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={updateClassMutation.isPending}
                    className="text-white hover:bg-white/10"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <ClassForm
                onSubmit={onSubmitClass}
                onCancel={handleCancel}
                isSubmitting={updateClassMutation.isPending}
                defaultValues={{
                  name: classData.name || '',
                  description: classData.description || '',
                  location_id: classData.location_id || null,
                  capacity: classData.max_students || 1,
                  billing_model: (
                    classData.billing_model === 'monthly' ||
                    classData.billing_model === 'per_session' ||
                    classData.billing_model === 'weekly' ||
                    classData.billing_model === 'per_class' ||
                    classData.billing_model === 'annual'
                  ) ? classData.billing_model : 'monthly',
                  price_dollars: classData.price_cents !== undefined ? centsToDollars(classData.price_cents) : '0.00',
                  season_id: classData.season_id || null,
                  level: classData.level || '',
                  dress_code: classData.dress_code || '',
                  category: classData.category || '',
                  status: classData.status || 'active',
                  trial_available: classData.trial_available || false,
                  is_active: classData.is_active !== undefined ? classData.is_active : true,
                  waitlistEnabled: classData.waitlist_enabled !== undefined ? classData.waitlist_enabled : true,
                  schedules: classData.schedules || [
                    { weekday: 1, start_time: '17:00', end_time: '18:00', rrule: '' }
                  ],
                }}
                locationOptions={locationOptions}
                seasonOptions={seasonOptions}
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default EditClassPage; 